import { Button } from '../../../components/ui/button';
import { HeadlinePinButton } from '../../../components/ui/HeadlinePinButton';
import type {
  GoogleAssets,
  GoogleHeadline,
  HeadlinePinPosition,
} from '../../../types/campaign';
import { estimateResolvedLength, hasPlaceholders } from '../placeholderEstimate';
import { PlaceholderHint } from './PlaceholderHint';

// Google RSA assets editor. Spec §3: headlines 3-15, descriptions 2-4, path1
// required, path2 optional.
//
// Note: Google's final destination URL is DERIVED, not authored here --
// see server/modules/campaigns/finalUrl.ts (the derivation runs at
// AccountCampaign snapshot in P2b). The source of truth is the Google
// landing page override rendered in CampaignForm just below this editor.
//
// Chunk F amendment: per-entry char caps are SOFT for template-bearing
// fields. Google's rendered limits are 30 (headline) and 90 (description),
// but authored strings count the raw placeholder tokens, which resolve
// shorter per location. The editor shows both (X authored / Y resolved
// estimate) and amber-warns when authored length exceeds the rendered
// target. The HARD cap is the schema's sanity ceiling (200 / 1000) which
// the UI only triggers red when exceeded.
//
// Per-location overflow validation happens at AccountCampaign render in P2b.

const HEADLINES_MIN = 3;
const HEADLINES_MAX = 15;
const HEADLINE_TARGET_CHARS = 30; // Google RSA rendered limit
const HEADLINE_SANITY_CAP = 200; // matches googleAssetsSchema
const DESCRIPTIONS_MIN = 2;
const DESCRIPTIONS_MAX = 4;
const DESCRIPTION_TARGET_CHARS = 90;
const DESCRIPTION_SANITY_CAP = 1000;
const PATH_MAX_CHARS = 15;

export type GoogleAssetsEditorProps = {
  value: GoogleAssets;
  onChange: (next: GoogleAssets) => void;
};

// Shared length-display pill for templated Google fields.
function TemplatedLengthHint({
  value,
  targetChars,
  sanityCap,
}: {
  value: string;
  targetChars: number;
  sanityCap: number;
}) {
  const authored = value.length;
  const estimated = estimateResolvedLength(value);
  const overSanity = authored > sanityCap;
  const overTarget = estimated > targetChars;
  const color = overSanity
    ? 'text-red-600'
    : overTarget
      ? 'text-amber-600'
      : 'text-slate-500';

  const hint = overSanity
    ? ` — over sanity cap ${sanityCap}, will reject on save`
    : overTarget
      ? ` — may be rejected by Google for some locations (render target ${targetChars})`
      : '';

  const showEstimate = hasPlaceholders(value);
  return (
    <div className={`text-xs ${color}`}>
      {authored} chars
      {showEstimate && ` (~${estimated} resolved)`}
      {hint}
    </div>
  );
}

export function GoogleAssetsEditor({ value, onChange }: GoogleAssetsEditorProps) {
  const update = <K extends keyof GoogleAssets>(key: K, v: GoogleAssets[K]) => {
    onChange({ ...value, [key]: v });
  };
  // Pinned Headlines spec §1: headline edits split into text vs pin
  // mutators so the pin button (added in a later commit) can target
  // pinPosition without copy-paste of the array splice logic.
  const updateHeadlineTextAt = (i: number, text: string) => {
    const next = value.headlines.map((h, idx) =>
      idx === i ? { ...h, text } : h,
    );
    update('headlines', next);
  };
  const updateHeadlinePinAt = (i: number, pinPosition: HeadlinePinPosition) => {
    const next = value.headlines.map((h, idx) =>
      idx === i ? { ...h, pinPosition } : h,
    );
    update('headlines', next);
  };
  const removeHeadlineAt = (i: number) => {
    update(
      'headlines',
      value.headlines.filter((_, idx) => idx !== i),
    );
  };
  const addHeadline = () => {
    if (value.headlines.length >= HEADLINES_MAX) return;
    const fresh: GoogleHeadline = { text: '', pinPosition: null };
    update('headlines', [...value.headlines, fresh]);
  };
  const updateDescriptionAt = (i: number, v: string) => {
    const next = [...value.descriptions];
    next[i] = v;
    update('descriptions', next);
  };
  const removeDescriptionAt = (i: number) => {
    update(
      'descriptions',
      value.descriptions.filter((_, idx) => idx !== i),
    );
  };
  const addDescription = () => {
    if (value.descriptions.length >= DESCRIPTIONS_MAX) return;
    update('descriptions', [...value.descriptions, '']);
  };

  return (
    <div className="space-y-6">
      {/* Headlines */}
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <label className="text-sm font-medium text-slate-900">
            Headlines ({HEADLINES_MIN}-{HEADLINES_MAX})
          </label>
          <span className="text-xs text-slate-500">
            {value.headlines.length} / {HEADLINES_MAX}
          </span>
        </div>
        {value.headlines.length < HEADLINES_MIN && (
          <p className="text-xs text-amber-700">
            Google requires at least {HEADLINES_MIN} headlines to activate. First three are seeded
            from the Brand's boilerplate.
          </p>
        )}
        {value.headlines.map((h, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="flex-1 space-y-1">
              <input
                type="text"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={h.text}
                onChange={(e) => updateHeadlineTextAt(i, e.target.value)}
                placeholder={`Headline ${i + 1}`}
              />
              <TemplatedLengthHint
                value={h.text}
                targetChars={HEADLINE_TARGET_CHARS}
                sanityCap={HEADLINE_SANITY_CAP}
              />
            </div>
            {/* Pinned Headlines spec §2.1: cycle pin position 1 → 2 →
                3 → unpinned. Multiple headlines may share the same
                position; no uniqueness validation. */}
            <div className="mt-1.5">
              <HeadlinePinButton
                value={h.pinPosition}
                onChange={(next) => updateHeadlinePinAt(i, next)}
              />
            </div>
            <button
              type="button"
              className="text-xs text-red-600 hover:text-red-800 mt-2"
              onClick={() => removeHeadlineAt(i)}
            >
              Remove
            </button>
          </div>
        ))}
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={addHeadline}
            disabled={value.headlines.length >= HEADLINES_MAX}
          >
            Add headline
          </Button>
        </div>
        <PlaceholderHint />
      </div>

      {/* Descriptions */}
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <label className="text-sm font-medium text-slate-900">
            Descriptions ({DESCRIPTIONS_MIN}-{DESCRIPTIONS_MAX})
          </label>
          <span className="text-xs text-slate-500">
            {value.descriptions.length} / {DESCRIPTIONS_MAX}
          </span>
        </div>
        {value.descriptions.length < DESCRIPTIONS_MIN && (
          <p className="text-xs text-amber-700">
            Google requires at least {DESCRIPTIONS_MIN} descriptions to activate.
          </p>
        )}
        {value.descriptions.map((v, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="flex-1 space-y-1">
              <input
                type="text"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                value={v}
                onChange={(e) => updateDescriptionAt(i, e.target.value)}
                placeholder={`Description ${i + 1}`}
              />
              <TemplatedLengthHint
                value={v}
                targetChars={DESCRIPTION_TARGET_CHARS}
                sanityCap={DESCRIPTION_SANITY_CAP}
              />
            </div>
            <button
              type="button"
              className="text-xs text-red-600 hover:text-red-800 mt-2"
              onClick={() => removeDescriptionAt(i)}
            >
              Remove
            </button>
          </div>
        ))}
        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={addDescription}
            disabled={value.descriptions.length >= DESCRIPTIONS_MAX}
          >
            Add description
          </Button>
        </div>
        <PlaceholderHint />
      </div>

      {/* Paths */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-900">Path 1 (required)</label>
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={value.path1}
            onChange={(e) => update('path1', e.target.value)}
            maxLength={PATH_MAX_CHARS * 2}
          />
          <div
            className={`text-xs ${
              value.path1.length > PATH_MAX_CHARS ? 'text-red-600' : 'text-slate-500'
            }`}
          >
            {value.path1.length} / {PATH_MAX_CHARS} characters. No placeholders -- Google's display URL is static.
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-900">Path 2 (optional)</label>
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={value.path2 ?? ''}
            onChange={(e) => update('path2', e.target.value === '' ? null : e.target.value)}
            maxLength={PATH_MAX_CHARS * 2}
          />
          <div
            className={`text-xs ${
              (value.path2 ?? '').length > PATH_MAX_CHARS ? 'text-red-600' : 'text-slate-500'
            }`}
          >
            {(value.path2 ?? '').length} / {PATH_MAX_CHARS} characters. Leave blank to omit.
          </div>
          <p className="text-xs text-slate-500">
            Google Path 2 doesn't allow spaces. Use <code className="font-mono">{'{suburbSlug}'}</code>{' '}
            for the suburb name (renders as 'perth-city' style). Or use{' '}
            <code className="font-mono">{'{shortSuburb}'}</code> for a shorter version (no
            auto-hyphen — make sure your shortSuburb values don't contain spaces).
          </p>
        </div>
      </div>
    </div>
  );
}
